import React, { useEffect, useState } from "react";
import { useProductsOffers } from "../../../../../../../../context/OrderCreator/ProductContext"; // ✅ بدل fetchProducts
import AddProductDialog from "./AddProductDialog";
import ProductFilters from "./ProductFilters";
import CategorySelector from "./CategorySelector";
import ProductGrid from "./ProductGrid";
import ActiveOffersList from "./ActiveOffersList";
import ProductFavoritesList from "./ProductFavoritesList";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StarIcon from "@mui/icons-material/Star";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useOrder } from "../../../../../../../../context/OrderCreator/TablesContext/OrderContext";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Skeleton,
} from "@mui/material";

const ProductBrowser = ({ orderId, onSuccess }) => {
  // ✅ استخدم البيانات من ProductContext
  const { products, loading, error, refresh } = useProductsOffers();

  const [selectedKitchen, setSelectedKitchen] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemData, setItemData] = useState({ quantity: "1", note: "", price: "" });
  const [sending, setSending] = useState(false);
  const [addedOffer, setAddedOffer] = useState(null);
  const [sendToKitchen, setSendToKitchen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // useOrder might contain additional product metadata (structure like: { data: [ { id, name, categories: [ { products: [...] } ] } ] })
  const orderCtx = useOrder();
  const { addItem } = orderCtx || {};

  // helper: find product inside orderCtx.data (if present)
  const findProductInOrderData = (productId) => {
    if (!orderCtx || !Array.isArray(orderCtx.data)) return null;
    for (const kitchen of orderCtx.data) {
      if (!kitchen || !Array.isArray(kitchen.categories)) continue;
      for (const cat of kitchen.categories) {
        if (!cat || !Array.isArray(cat.products)) continue;
        const found = cat.products.find((p) => String(p.id) === String(productId));
        if (found) return found;
      }
    }
    return null;
  };

  const subdomain = window.location.hostname.split('.')[0];
  const IMAGE_BASE_URL = `https://${subdomain}.restocash.app/`;
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";

  // ✅ تحديد أول مطبخ تلقائي بعد تحميل المنتجات
  useEffect(() => {
    if (!selectedKitchen && Array.isArray(products) && products.length > 0) {
      setSelectedKitchen(products[0]);
    }
  }, [products]);

  // 🔍 تصفية التصنيفات حسب البحث
  const getFilteredCategories = () => {
    if (!selectedKitchen || !selectedKitchen.categories) return [];
    if (!search.trim()) return selectedKitchen.categories;

    const searchTerm = search.trim().toLowerCase();
    return selectedKitchen.categories
      .map((cat) => {
        const categoryNameMatches = cat.name?.toLowerCase().includes(searchTerm);
        const filteredProducts = (cat.products || []).filter(
          (prod) =>
            prod.name?.toLowerCase().includes(searchTerm) ||
            prod.description?.toLowerCase().includes(searchTerm)
        );
        return categoryNameMatches ? { ...cat } : { ...cat, products: filteredProducts };
      })
      .filter(
        (cat) =>
          cat.name?.toLowerCase().includes(searchTerm) ||
          (cat.products && cat.products.length > 0)
      );
  };

  // ✅ عند إضافة عرض
  const handleOfferAdded = (offer) => {
    setAddedOffer(offer || {});
    if (typeof onSuccess === "function") {
      onSuccess({
        offer_name: offer?.name,
        offer_total: offer?.total_price,
      });
    }
  };

  const handleSendToKitchenChange = (e) => {
    setSendToKitchen(e.target.checked);
  };

  // ✅ إرسال صنف للطلب
  const handleSubmit = async () => {
    if (!orderId || !selectedProduct) {
      setSnack({
        open: true,
        message: "حدث خطأ: الطلب أو المنتج غير موجود.",
        severity: "error",
      });
      return;
    }

    setSending(true);
    try {
      const item = {
        product_id: selectedProduct.id,
        quantity: parseInt(itemData.quantity, 10),
        note: itemData.note,
        price: parseFloat(itemData.price || selectedProduct.price),
        status: sendToKitchen ? "pending" : "finished",
      };

      const res = await addItem(orderId, item, selectedProduct);

      setOpenDialog(false);
      setItemData({ quantity: "1", note: "", price: "" });
      setSnack({
        open: true,
        message: res?.message || "تمت الإضافة بنجاح",
        severity: "success",
      });

      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      setSnack({
        open: true,
        message: err.message || "فشل في إضافة الصنف",
        severity: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const getFilteredSelectedCategory = () => {
    if (!selectedCategory) return null;
    const filteredCategories = getFilteredCategories();
    return filteredCategories.find(cat => cat.id === selectedCategory.id) || null;
  };

  // ✅ حالات التحميل والخطأ
  if (loading) return <Skeleton variant="rectangular" height={400} />;
  if (error) return <Typography color="error">❌ {error.message || "فشل في جلب المنتجات"}</Typography>;

  // NEW: دالة للتعامل مع ضغطات المستخدم على المجموعات
  const handleCategoryClick = (cat) => {
    if (expandedCategory?.id === cat.id) {
      // إذا كانت المجموعة مفتوحة، أغلقها
      setExpandedCategory(null);
      setSelectedCategory(null);
    } else {
      // افتح المجموعة الجديدة
      setExpandedCategory(cat);
      setSelectedCategory(cat);
    }
  };

  // جلب جميع المجموعات (بما فيها العروض والمفضلة)
  const getAllCategories = () => {
    if (!selectedKitchen) return [];
    const filteredCats = getFilteredCategories();
    return [
      { id: "offers", name: "العروض", products: [] },
      { id: "favorites", name: "المفضلة", products: [] },
      ...filteredCats,
    ];
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>

      <AddProductDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSubmit={handleSubmit}
        product={selectedProduct}
        quantity={itemData.quantity}
        note={itemData.note}
        price={itemData.price || selectedProduct?.price}
        onQuantityChange={(e) => setItemData((prev) => ({ ...prev, quantity: e.target.value }))}
        onNoteChange={(e) => setItemData((prev) => ({ ...prev, note: e.target.value }))}
        onPriceChange={(e) => setItemData((prev) => ({ ...prev, price: e.target.value }))}
        loading={sending}
        sendToKitchen={sendToKitchen}
        onSendToKitchenChange={handleSendToKitchenChange}
        status={sendToKitchen ? "pending" : "finished"}
        // NEW props
        preparing_time={selectedProduct?.preparing_time}
        is_preparing={selectedProduct?.is_preparing}
      />

      <ProductFilters
        data={products}
        selectedKitchen={selectedKitchen}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        onKitchenChange={setSelectedKitchen}
      />

      {selectedKitchen && (
        <Box>
          {/* عرض المجموعات في صفوف (5 في كل صف) */}
          {!expandedCategory && (
            <CategorySelector
              kitchen={{
                ...selectedKitchen,
                categories: getFilteredCategories(),
              }}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategoryClick}
              orderId={orderId}
            />
          )}

          {/* عرض المجموعة المفتوحة وأصنافها */}
          {expandedCategory && (
            <Box>
              {/* عرض المجموعة المفتوحة + باقي المجموعات */}
              <Box
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 1.2, md: 1.5 },
                  mb: { xs: 1.5, sm: 1.8, md: 2 },
                  overflowX: "auto",
                  pb: { xs: 0.8, sm: 1 },
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { height: { xs: 4, sm: 5 } },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "#fbc2eb", borderRadius: 3 },
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {/* المجموعة المفتوحة */}
                <Box
                  onClick={() => handleCategoryClick(expandedCategory)}
                  sx={{
                    minWidth: { xs: 90, sm: 100, md: 110, lg: 120 },
                    maxWidth: { xs: 110, sm: 120, md: 130, lg: 140 },
                    p: { xs: 1.5, sm: 1.5, md: 1.5 },
                    borderRadius: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, rgba(251, 194, 235, 0.15) 0%, rgba(26, 26, 46, 0.95) 100%)",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    border: "2px solid rgba(251, 194, 235, 0.8)",
                    boxShadow: "0 0 0 2px rgba(251, 194, 235, 0.2), 0 6px 20px rgba(251, 194, 235, 0.3)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 0.3, sm: 0.4, md: 0.5 },
                    touchAction: "manipulation",
                    "&:hover": {
                      transform: "translateY(-3px) scale(1.02)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: { xs: "2.5px", sm: "3px" },
                      background: "linear-gradient(90deg, #fbc2eb 0%, #ff9a9e 100%)",
                    },
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 32, sm: 34, md: 36 },
                      height: { xs: 32, sm: 34, md: 36 },
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #fbc2eb 0%, #ff9a9e 100%)",
                      boxShadow: "0 3px 10px rgba(251, 194, 235, 0.4)",
                    }}
                  >
                    {expandedCategory.id === "offers" ? (
                      <LocalOfferIcon sx={{ fontSize: { xs: 18, sm: 19, md: 20 }, color: "#000" }} />
                    ) : expandedCategory.id === "favorites" ? (
                      <StarIcon sx={{ fontSize: { xs: 18, sm: 19, md: 20 }, color: "#000" }} />
                    ) : (
                      <RestaurantMenuIcon sx={{ fontSize: { xs: 18, sm: 19, md: 20 }, color: "#000" }} />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: 12, sm: 12.5, md: 13 },
                      color: "#fbc2eb",
                      letterSpacing: "0.3px",
                      lineHeight: 1.2,
                    }}
                  >
                    {expandedCategory.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 9.5, md: 10 },
                      color: "rgba(251, 194, 235, 0.9)",
                      mt: 0,
                    }}
                  >
                    انقر للإغلاق
                  </Typography>
                </Box>

                {/* باقي المجموعات (مصغّرة) */}
                {getAllCategories()
                  .filter((cat) => cat.id !== expandedCategory.id)
                  .map((cat) => {
                    const Icon =
                      cat.id === "offers"
                        ? LocalOfferIcon
                        : cat.id === "favorites"
                        ? StarIcon
                        : RestaurantMenuIcon;

                    return (
                      <Box
                        key={cat.id}
                        onClick={() => handleCategoryClick(cat)}
                        sx={{
                          minWidth: { xs: 70, sm: 75, md: 80, lg: 85 },
                          maxWidth: { xs: 85, sm: 95, md: 100, lg: 105 },
                          p: { xs: 0.8, sm: 1, md: 1 },
                          borderRadius: 2.5,
                          textAlign: "center",
                          cursor: "pointer",
                          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                          backdropFilter: "blur(10px)",
                          color: "#fff",
                          border: "1.5px solid rgba(255, 255, 255, 0.08)",
                          boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: { xs: 0.2, sm: 0.3 },
                          touchAction: "manipulation",
                          "&:hover": {
                            border: "2px solid rgba(251, 194, 235, 0.6)",
                            transform: "translateY(-3px) scale(1.05)",
                            boxShadow: "0 10px 28px rgba(251, 194, 235, 0.25)",
                            background: "linear-gradient(135deg, rgba(251, 194, 235, 0.2) 0%, rgba(26, 26, 46, 0.95) 100%)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 26, sm: 28, md: 30 },
                            height: { xs: 26, sm: 28, md: 30 },
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(255, 255, 255, 0.06)",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <Icon sx={{ fontSize: { xs: 14, sm: 15, md: 16 }, color: "#fbc2eb" }} />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: 10, sm: 10.5, md: 11 },
                            color: "#fff",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                            lineHeight: 1.2,
                          }}
                        >
                          {cat.name}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>

              {/* عرض الأصناف أو العروض أو المفضلة */}
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                }}
              >
                {expandedCategory.id === "offers" ? (
                  <ActiveOffersList orderId={orderId} onOfferAdded={handleOfferAdded} />
                ) : expandedCategory.id === "favorites" ? (
                  <ProductFavoritesList
                    imageBaseUrl={IMAGE_BASE_URL}
                    placeholderImage={placeholderImage}
                    onSelectProduct={(prod) => {
                      setSelectedProduct(prod);
                      setItemData({ quantity: "1", note: "", price: prod.price || "" });
                      setSendToKitchen(Boolean(prod?.is_preparing));
                      setOpenDialog(true);
                    }}
                  />
                ) : (
                  (() => {
                    const filteredCategory = getFilteredSelectedCategory();
                    if (
                      filteredCategory &&
                      Array.isArray(filteredCategory.products) &&
                      filteredCategory.products.length > 0
                    ) {
                      return (
                        <ProductGrid
                          category={filteredCategory}
                          onSelectProduct={(prod) => {
                            setSelectedProduct(prod);
                            setItemData({ quantity: "1", note: "", price: prod.price || "" });
                            setSendToKitchen(Boolean(prod?.is_preparing));
                            setOpenDialog(true);
                          }}
                          imageBaseUrl={IMAGE_BASE_URL}
                          placeholderImage={placeholderImage}
                        />
                      );
                    }
                    return null;
                  })()
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProductBrowser;
